import { useCallback, useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]", "button:not([disabled])", "input:not([disabled])",
  "select:not([disabled])", "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])",
].join(",");

/** Keyboard behavior for a non-modal top-bar popover. It deliberately does not trap focus. */
export default function useAccessiblePopover(open, onClose) {
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  const closeAndRestore = useCallback(() => {
    closeRef.current?.();
    window.requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => {
      panelRef.current?.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
    });
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeAndRestore();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closeAndRestore]);

  return { panelRef, triggerRef, closeAndRestore };
}

